import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api/api';
import AuthContext from '../../contexts/AuthContext';
import '../../styles/ParentNewMessagePage.css';

const ParentNewMessagePage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [messageContent, setMessageContent] = useState('');

  // Fetch teachers when component mounts
  useEffect(() => {
    fetchTeachers();
  }, []);

  // Fetch teachers assigned to parent's children
  const fetchTeachers = async () => {
    setLoading(true);
    try {
      // Use the parent-specific endpoint to get available message recipients
      const response = await api.get('/messages/recipients');
      console.log('Parent message recipients response:', response.data);

      let teachersData = [];
      if (response.data && response.data.success) {
        teachersData = response.data.data || [];
      } else if (Array.isArray(response.data)) {
        teachersData = response.data;
      }

      console.log('Teachers data:', teachersData);

      // Filter out non-teacher recipients
      const filteredTeachers = teachersData.filter(t => 
        t.role === 'teacher' || 
        t.role === 'admin' || 
        t.recipientType === 'teacher'
      );

      // Deduplicate teachers by _id
      const uniqueTeachers = {};
      filteredTeachers.forEach(teacher => {
        const key = teacher._id;
        if (!uniqueTeachers[key]) {
          uniqueTeachers[key] = { 
            ...teacher, 
            // Initialize childNames array if not exists
            childNames: teacher.childName ? [teacher.childName] : []
          };
        } else if (teacher.childName && !uniqueTeachers[key].childNames.includes(teacher.childName)) {
          uniqueTeachers[key].childNames.push(teacher.childName);
        }
      });

      const teachersList = Object.values(uniqueTeachers);
      console.log('Processed teachers list:', teachersList);

      setTeachers(teachersList);
      setFilteredTeachers(teachersList);
      setError(null);
    } catch (err) {
      console.error('Error fetching teachers:', err);
      setError('Failed to load teachers. Please try again later.');
      setTeachers([]);
      setFilteredTeachers([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter teachers based on search text
  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearchText(searchValue);

    if (!searchValue.trim()) {
      setFilteredTeachers(teachers);
      return;
    }

    const searchLower = searchValue.toLowerCase();
    const filtered = teachers.filter(teacher => {
      // Search by name
      return teacher.name && teacher.name.toLowerCase().includes(searchLower);
    });

    setFilteredTeachers(filtered);
  };

  // Handle teacher selection
  const handleSelectTeacher = (teacher) => {
    setSelectedTeacher(teacher);
  };

  // Update message content
  const handleMessageChange = (e) => {
    setMessageContent(e.target.value);
  };

  // Process template placeholders
  const processPlaceholders = (text) => {
    if (!text) return '';
    
    // Basic placeholder replacements
    let processed = text;
    
    processed = processed.replace(/{{parentName}}/g, user?.name || 'Parent');
    
    // Handle student name if we have it
    if (user?.parentInfo?.children && user.parentInfo.children.length > 0) {
      const childName = typeof user.parentInfo.children[0] === 'object' 
        ? user.parentInfo.children[0].name 
        : 'Student';
      processed = processed.replace(/{{studentName}}/g, childName);
    } else {
      processed = processed.replace(/{{studentName}}/g, 'Student');
    }
    
    return processed;
  };

  // Send message
  const handleSendMessage = async () => {
    if (!selectedTeacher || !messageContent.trim()) {
      setError('Please select a teacher and enter a message');
      return;
    }

    setSending(true);
    try {
      // Process content with placeholders
      const processedContent = processPlaceholders(messageContent);
      
      // Send message using the API
      await api.post('/messages', {
        recipient: selectedTeacher._id,
        content: processedContent,
        subject: 'Message from Parent'
      });

      // Navigate back to messages list on success
      navigate('/messages');
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Go back to compose view
  const handleBackToTeacherList = () => {
    setSelectedTeacher(null);
  };

  // Render teacher list view
  const renderTeacherList = () => {
    return (
      <div className="teacher-selection-container">
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search teachers..."
            value={searchText}
            onChange={handleSearch}
          />
        </div>
        
        {filteredTeachers.length === 0 ? (
          <div className="empty-state">
            <p>No teachers found</p>
            {searchText ? (
              <p className="empty-state-hint">Try a different search term</p>
            ) : (
              <p className="empty-state-hint">No teachers are assigned to your children</p>
            )}
          </div>
        ) : (
          <div className="teachers-list">
            {filteredTeachers.map(teacher => (
              <div 
                key={teacher._id} 
                className="teacher-card"
                onClick={() => handleSelectTeacher(teacher)}
              >
                <div className="teacher-avatar">
                  {teacher.name ? teacher.name.charAt(0).toUpperCase() : 'T'}
                </div>
                <div className="teacher-info">
                  <h3 className="teacher-name">{teacher.name || 'Teacher'}</h3>
                  <p className="teacher-role">Teacher</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render message composition view
  const renderComposeView = () => {
    return (
      <div className="compose-container">
        <div className="recipient-header">
          <button 
            className="back-to-list-btn"
            onClick={handleBackToTeacherList}
          >
            ← Back to teachers
          </button>
          
          <div className="selected-teacher">
            <div className="teacher-avatar large">
              {selectedTeacher.name ? selectedTeacher.name.charAt(0).toUpperCase() : 'T'}
            </div>
            <div className="teacher-info">
              <h3 className="teacher-name">{selectedTeacher.name || 'Teacher'}</h3>
              <p className="teacher-role">Teacher</p>
            </div>
          </div>
        </div>
        
        <div className="message-area">
          <label htmlFor="message-content">Message</label>
          <textarea
            id="message-content"
            className="message-input"
            value={messageContent}
            onChange={handleMessageChange}
            placeholder="Type your message here..."
            rows="8"
          ></textarea>
        </div>
        
        <div className="action-buttons">
          <button 
            className="cancel-btn"
            onClick={handleBackToTeacherList}
          >
            Cancel
          </button>
          <button 
            className="send-btn"
            onClick={handleSendMessage}
            disabled={sending || !messageContent.trim()}
          >
            {sending ? 'Sending...' : 'Send Message'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="parent-new-message-container">
      <div className="header">
        <h1>New Message</h1>
        <Link to="/messages" className="back-btn">
          Back to Messages
        </Link>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button 
            className="retry-button"
            onClick={fetchTeachers}
          >
            Try Again
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading teachers...</p>
        </div>
      ) : selectedTeacher ? (
        renderComposeView()
      ) : (
        renderTeacherList()
      )}
    </div>
  );
};

export default ParentNewMessagePage;